<template>
    <div class="listing-layout flex-column hp-10">
        <!-- LOADER -->
        <preloader :display="loading" class="preloader prelative" :background="false" />

        <!-- HEADER -->
        <header class="flex-level mb-4 h-4">
            <!-- TITLE -->
            <h1 v-if="$slots['header'] !== '' || metaTitle !== ''" class="m-0">
                <slot name="header">{{ metaTitle }}</slot>
            </h1>

            <!-- ACTIONS -->
            <div class="flex-right">
                <ul class="action-nav">
                    <li v-if="meltingSpotWidgetId">
                        <MeltingSpotWidget :widgetId="meltingSpotWidgetId" />
                    </li>
                    <li v-show="hasKpis">
                        <div class="flex-level">
                            <p class="m-0 mr-2">{{ $l10n.__('Afficher les statistiques') }}</p>
                            <CompassSwitch
                                :modelValue="displayKpi"
                                size="medium"
                                @click="toggleKPI"
                            />
                        </div>
                    </li>
                    <li v-if="enableExport">
                        <CompassMenu placement="bottom-end" customClass="listing-nav-actions-menu">
                            <CompassButton
                                :label="$t.__('common.labels.actions')"
                                type="secondary"
                                dropdown
                            />
                            <template #dropdown>
                                <CompassMenuItem
                                    v-for="(exportFormat, index) in exportFormats"
                                    :key="index"
                                    :label="$l10n.__('Export ' + exportFormat.toUpperCase())"
                                    :disabled="!permissions.export"
                                    @click="
                                        $store.commit(`${listinghash}/toggleDisplay`, 'export');
                                        $store.commit(
                                            `${listinghash}/updateExportType`,
                                            exportFormat,
                                        );
                                    "
                                />
                                <CompassMenuItem
                                    v-for="(button, index) in secondaryButtons"
                                    :key="index"
                                    :label="button.label"
                                    :disabled="button.disabled"
                                    @click="onButtonClick(button)"
                                />
                            </template>
                        </CompassMenu>
                    </li>
                    <li v-if="!enableExport && secondaryButtons.length > 0">
                        <CompassTooltip
                            v-for="button in secondaryButtons"
                            :key="button.label"
                            :content="$t.__('common.labels.no_necessary_rights')"
                            placement="top"
                            :disabled="!button.disabled"
                        >
                            <CompassMenu
                                v-if="button.items && button.items.length > 0"
                                placement="bottom-end"
                                customClass="listing-nav-actions-menu"
                            >
                                <CompassButton
                                    type="secondary"
                                    :label="button.label"
                                    :disabled="button.disabled"
                                    dropdown
                                />
                                <template #dropdown>
                                    <CompassMenuItem
                                        v-for="(item, index) in button.items"
                                        :key="index"
                                        :prefixIcon="item.icon"
                                        :label="item.label"
                                        :disabled="item.disabled"
                                        :data-bot="item.databot ? item.databot : ''"
                                        @click="onButtonClick(item)"
                                    />
                                </template>
                            </CompassMenu>
                            <CompassButton
                                v-else
                                type="secondary"
                                :label="button.label"
                                :disabled="button.disabled"
                                :data-bot="button.databot ? button.databot : ''"
                                @click="onButtonClick(button)"
                            />
                        </CompassTooltip>
                    </li>
                    <li v-for="button in primaryButtons" :key="button.label">
                        <CompassTooltip
                            :content="$t.__('common.labels.no_necessary_rights')"
                            placement="top"
                            :disabled="!button.disabled"
                        >
                            <CompassMenu
                                v-if="button.items && button.items.length > 0"
                                placement="bottom-end"
                                customClass="listing-nav-actions-menu"
                            >
                                <CompassButton
                                    :label="button.label"
                                    :disabled="button.disabled"
                                    :data-bot="button.databot ? button.databot : ''"
                                    dropdown
                                />
                                <template #dropdown>
                                    <CompassMenuItem
                                        v-for="(item, index) in button.items"
                                        :key="index"
                                        :prefixIcon="item.icon"
                                        :label="item.label"
                                        :disabled="item.disabled"
                                        :data-bot="item.databot ? item.databot : ''"
                                        :tooltipProps="{
                                            disabled: !item.disabled,
                                            arrow: false,
                                            placement: 'top',
                                            content: $t.__('common.labels.no_necessary_rights'),
                                        }"
                                        @click="onButtonClick(item)"
                                    />
                                </template>
                            </CompassMenu>
                            <CompassButton
                                v-else
                                :label="button.label"
                                :disabled="button.disabled"
                                :data-bot="button.databot ? button.databot : ''"
                                @click="onButtonClick(button)"
                            />
                        </CompassTooltip>
                    </li>
                </ul>
            </div>
        </header>
        <!-- HEADER END -->

        <!-- BODY -->
        <div class="listing prelative flex-column ov-reset mh-0 flex">
            <kpi-list
                v-if="displayKpi"
                class="kip-list"
                :kpis="kpis"
                type="listing"
                :source="linkedtype || name"
            ></kpi-list>

            <DraftNumberingAlert v-if="showDraftNumberingAlert" :counts="draftCounts" />

            <CompassAlert
                v-if="showInvoicingHistoryModeAlert"
                type="error"
                :description="$t.__('document.DocFormLayout.alert_import_in_progress')"
                :showClose="false"
            />
            <!-- LISTING TOOLS -->
            <div class="listing-tools flex-level no-shrink mb-2">
                <!-- FILTER -->
                <div class="flex flex-level flex-top">
                    <CompassButton
                        v-if="enableFiltering"
                        :label="$t.__('common.actions.filter')"
                        @click.prevent="$store.dispatch(`${listinghash}/toggleDisplay`, 'filters')"
                    />
                    <filters-preview v-if="!display.filters" :listinghash="listinghash" />
                </div>

                <!-- PAGINATION -->
                <paginator v-if="!isMap" :pagination="pagination" @pagination:update="goToPage" />

                <!-- OPTIONS -->
                <CompassMenu
                    v-if="enableOptions && !isMap"
                    trigger="hover"
                    customClass="listing-nav-actions-menu"
                >
                    <CompassIconButton icon="settings-fill" size="small" />
                    <template #dropdown>
                        <CompassMenuItem
                            :label="$l10n.__('Gestion des lignes')"
                            @click="$store.commit(`${listinghash}/toggleDisplay`, 'table')"
                        />
                        <CompassMenuItem
                            :label="$l10n.__('Gestion des colonnes')"
                            @click="$store.commit(`${listinghash}/toggleDisplay`, 'columns')"
                        />
                    </template>
                </CompassMenu>
                <CompassToggleButtonGroup
                    v-if="switchDisplay && switchDisplay.length > 0"
                    :modelValue="activeToggleButton"
                    exclusive
                    size="small"
                    class="ml-2"
                >
                    <CompassTooltip
                        v-for="(switchButton, switchIndex) in switchDisplay"
                        :key="switchIndex"
                        :content="switchButton.label"
                        :arrow="false"
                    >
                        <CompassToggleButton
                            :value="switchButton.id"
                            :icon="switchButton.icon"
                            :data-upsell="
                                switchButton.id === 'map-view' ? 'map_listing' : undefined
                            "
                            @click="
                                handle_function_call(
                                    switchButton.action,
                                    switchButton.id,
                                    switchButton.selected,
                                )
                            "
                        />
                    </CompassTooltip>
                </CompassToggleButtonGroup>
            </div>
            <!-- LISTING TOOLS END -->

            <!-- LISTING & FILTER PANEL -->
            <div class="flex-row mh-0 flex">
                <!-- FILTER PANEL -->
                <div
                    v-show="display.filters && enableFiltering"
                    class="leftpane well well-no-shadow mr-4 flex-column"
                >
                    <filters-pane v-if="!filtersComponent" :listinghash="listinghash" />
                    <company-directory-filters v-else :data="params" :listinghash="listinghash" />
                </div>

                <!-- COLUMN PANEL -->
                <transition name="slide-fade">
                    <listing-columns v-if="display.columns" :listinghash="listinghash" />
                </transition>

                <!-- DISPLAY OPTION PANEL -->
                <transition name="slide-fade">
                    <listing-display v-if="display.table" :listinghash="listinghash" />
                </transition>

                <!-- LISTING -->
                <div class="listingContainer br flex flex-column">
                    <!-- <div class="listingContent prelative"> -->

                    <!-- ALERT -->
                    <div
                        v-if="alert.enabled && !!alert.text && alert.text.length > 0"
                        class="placeholder-alert placeholder primary-05-bkg b-1-primary-10 p-0 mb-2"
                    >
                        <p class="m-0 fs-3 aleft pl-3">{{ alert.text }}</p>
                    </div>

                    <!-- EMPTY RESULT -->
                    <div
                        v-if="
                            !isMap &&
                            !error &&
                            !loading &&
                            enableFiltering &&
                            Object.keys(rows).length == 0
                        "
                        class="placeholder"
                    >
                        <p class="acenter">
                            {{ $l10n.__('Aucun résultat ne correspond aux filtres appliqués') }}
                        </p>
                        <CompassButton
                            :label="$t.__('common.actions.reset')"
                            @click="$store.dispatch(`${listinghash}/resetListing`)"
                        />
                    </div>

                    <!-- EMPTY RESULT / NO FILTERING -->
                    <div
                        v-if="
                            !error && !loading && !enableFiltering && Object.keys(rows).length == 0
                        "
                        class="placeholder"
                    >
                        <p class="acenter">
                            {{ emptyResultPlaceholder }}
                        </p>
                    </div>

                    <!-- ERROR -->
                    <div v-if="error" class="placeholder">
                        <p class="acenter">
                            {{
                                $l10n.__(
                                    'Une erreur est survenue, veuillez réinitialiser la liste des filtres',
                                )
                            }}
                        </p>
                        <CompassButton
                            :label="$t.__('common.actions.reset')"
                            @click="$store.dispatch(`${listinghash}/resetListing`)"
                        />
                    </div>

                    <!-- TABLE COMPONENT -->
                    <ListingTableElement
                        v-if="!isMap && Object.keys(rows).length > 0"
                        ref="listingTable"
                        :listinghash="listinghash"
                        :rows="rows"
                        :columns="activeColumns"
                    />

                    <listing-map
                        v-if="isMap && !error"
                        :centermap="centermap"
                        :rows="rows"
                        :maptoken="maptoken"
                        :listinghash="listinghash"
                    ></listing-map>

                    <!-- </div> -->
                </div>
                <!-- LISTING END -->
            </div>
            <!-- LISTING  & FILTER PANEL END -->

            <listing-favoritesform
                v-if="display.favoritesForm"
                :listinghash="listinghash"
                :favoriteData="favoriteData"
            />

            <export-modal
                v-if="display.export"
                :listinghash="listinghash"
                :exportType="display.exportType"
                :exportOptions="exportOptions"
                :hasKpis="hasKpis && enableKpisOnExport"
            />
        </div>
        <!-- BODY END -->
    </div>
</template>

<script>
import Feature from 'Services/Feature';
import Env from 'Services/Env';
import ListingMap from './ListingMap.vue';
import FiltersPreview from './FiltersPreview.vue';
import FiltersPane from './FiltersPane.vue';
import Paginator from '../utilities/Paginator.vue';
import ListingTableElement from './ListingTableElement.vue';
import ListingFavoritesform from './favorites/ListingFavoriteForm.vue';
import ListingColumns from './ListingColumns.vue';
import ListingDisplay from './ListingDisplay.vue';
import KpiList from 'vcomponents/utilities/KpiList.vue';
import Preloader from 'vcomponents/utilities/Preloader.vue';
import CompanyDirectoryFilters from 'vcomponents/companysearch/CompanyDirectoryFilters.vue';
import ListingModule from 'vueDir/store/listing';
import MapModule from 'stores/maplisting';
import ApiListingModule from 'stores/apiListing';
import ExportModal from './ExportModal.vue';
import { guid, goToUrl } from 'vueDir/helpers';
import { mapState } from 'vuex';
import _, { isEqual } from 'lodash';
import ListingConfig from '../listing/config';
import ListingEmailRequest from './dto/ListingEmailRequest';
import { buildUrl } from '../../helpers';
import EmailMixins from 'mixins/EmailMixins';

const ConfigListings = new ListingConfig();

import MeltingSpotWidget from 'vcomponents/academy/MeltingSpotWidget.vue';
import LocalStorage from 'Core/commons/LocalStorage';
import { createNamespacedHelpers } from 'vuex';
import { defineAsyncComponent } from 'vue';

const popupPanelMapActions = createNamespacedHelpers('popupPanel').mapActions;
const { mapState: userMapState } = createNamespacedHelpers('user');

export default {
    components: {
        'company-directory-filters': CompanyDirectoryFilters,
        Preloader,
        FiltersPane,
        Paginator,
        ListingTableElement,
        FiltersPreview,
        ListingColumns,
        ListingDisplay,
        ExportModal,
        ListingFavoritesform,
        KpiList,
        ListingMap,
        DraftNumberingAlert: defineAsyncComponent(
            () => import('vcomponents/listing/DraftNumberingAlert.vue'),
        ),
        MeltingSpotWidget,
    },
    mixins: [EmailMixins],
    props: {
        endpoint: {
            type: String,
            required: true,
        },
        params: {
            type: Object,
            required: false,
            default: function () {
                return {};
            },
        },
        linkedtype: {
            type: String,
        },
        prefilter: {
            type: Object,
            required: false,
            default: function () {
                return {};
            },
        },
        enableFiltering: {
            type: Boolean,
            required: false,
            default: true,
        },
        enableExport: {
            type: Boolean,
            required: false,
            default: true,
        },
        enableKpisOnExport: {
            type: Boolean,
            required: false,
            default: true,
        },
        exportOptions: {
            type: Object,
            required: false,
            default: function () {
                return {};
            },
        },
        exportFormats: {
            type: Array,
            required: false,
            default: function () {
                return ['csv', 'pdf'];
            },
        },
        favorite: {
            required: false,
            type: Number,
            default: null,
        },
        favoritesName: {
            required: false,
            type: String,
            default: '',
        },
        name: {
            required: false,
            type: String,
            default: '',
        },
        filter: {
            required: false,
            type: Object,
            default() {
                return {};
            },
        },
        buttons: {
            required: false,
            type: Array,
            default() {
                return [];
            },
        },
        useFavorites: {
            type: Boolean,
            default: true,
        },
        savedSearchesUrl: {
            type: String,
            required: false,
        },
        filtersComponent: {
            type: String,
            required: false,
        },
        enableOptions: {
            type: Boolean,
            required: false,
            default: true,
        },
        switchDisplay: {
            type: [Array, Boolean],
            required: false,
            default: false,
        },
        listingMapping: {
            type: Object,
            required: false,
        },
        exportUrl: {
            required: false,
            type: String,
            default: null,
        },
        url: {
            required: false,
            type: String,
            default: null,
        },
        metaUrl: {
            required: false,
            type: String,
            default: null,
        },
        emptyResultPlaceholder: {
            required: false,
            type: String,
            default: window.i18n.__('Aucun élément lié'),
        },
        storeModule: {
            required: false,
            type: Object,
        },
        aggregations: {
            required: false,
            type: Array,
            default() {
                return [];
            },
        },
        enableContext: {
            required: false,
            type: Boolean,
            default: false,
        },
        alert: {
            required: false,
            type: Object,
            default: () => {
                return { text: null, enabled: false };
            },
        },
        relatedPermissions: {
            required: false,
            type: Object,
            default: () => {
                return {};
            },
        },
    },
    data() {
        return {
            listinghash: '',
            isMap: new LocalStorage(`${this.name}_listing`, 'viewmap').get(),
            nameListing: this.name,
            listingMapData: {},
            maptoken: Env.get('jawg').token,
            activeToggleButton: 'liste-view',
        };
    },
    computed: {
        ...mapState('corp', {
            centermap: 'mainAddress',
        }),
        ...userMapState({
            isAdmin: 'isAdmin',
        }),
        metaTitle() {
            return this.$route.meta && this.$route.meta.title != ''
                ? this.$route.meta.title
                : false;
        },
        primaryButtons() {
            return this.buttons.filter((button) => {
                return button.primary;
            });
        },

        secondaryButtons() {
            return this.buttons.filter((button) => {
                return button.primary == undefined || !button.primary;
            });
        },

        ajax() {
            return this.$store.state[this.listinghash].ajax;
        },

        error() {
            return this.$store.state[this.listinghash].error;
        },

        init() {
            return this.$store.state[this.listinghash].init;
        },

        rows() {
            if (typeof this.$store.state[this.listinghash].rows === 'object') {
                return Object.values(this.$store.state[this.listinghash].rows);
            }
            return this.$store.state[this.listinghash].rows;
        },

        pagination() {
            return this.$store.state[this.listinghash].pagination;
        },
        columns() {
            return this.$store.state[this.listinghash].columns;
        },
        display() {
            return this.$store.state[this.listinghash].display;
        },

        activeColumns() {
            return this.$store.getters[`${this.listinghash}/activeColumns`];
        },

        activeFilters() {
            return this.$store.getters[`${this.listinghash}/activeFilters`];
        },

        loading() {
            return !!this.ajax;
        },

        meta() {
            return this.$store.getters[`${this.listinghash}/meta`];
        },

        permissions() {
            return this.$store.getters[`${this.listinghash}/permissions`];
        },

        favoriteData() {
            return this.$store.state[this.listinghash].favoriteData;
        },

        kpis() {
            return this.$store.state[this.listinghash].kpis;
        },

        displayKpi() {
            return this.kpis.length > 0 && this.display.kpi;
        },

        hasKpis() {
            return this.$store.state[this.listinghash].hasKpis;
        },
        showDraftNumberingAlert() {
            return ['invoice', 'creditnote'].includes(this.linkedtype) && this.isAdmin;
        },
        draftCounts() {
            return this.$store.state[this.listinghash]?.draftCounts;
        },
        showInvoicingHistoryModeAlert() {
            return (
                Feature.isEnabled('invoicing-history-mode') &&
                ['invoice', 'creditnote'].includes(this.linkedtype)
            );
        },

        meltingSpotWidgetId() {
            if (!Feature.isEnabled('meltingspot-integration')) {
                return null;
            }

            const widgetIds = {
                opportunities: 'a2e9fcd5-f9a6-4a31-bd23-9057850d5a25',
                estimate: '3c9d7ad6-8ee9-437d-9aca-a3841befb830',
                invoice: 'dfd4e435-ebb4-4249-9582-541a6b98548d',
            };

            return widgetIds[this.$route.name] || null;
        },
    },

    watch: {
        $route(newRoute, oldRoute) {
            if (newRoute.path !== oldRoute.path) {
                this.initListing();
            }
        },
        draftCounts: {
            handler(newValue, oldValue) {
                if (
                    !newValue ||
                    typeof newValue.invoices !== 'number' ||
                    isEqual(newValue, oldValue)
                ) {
                    return;
                }

                const urlParams = new URLSearchParams(window.location.search);
                const showDraftNumberingModal = urlParams.get('showDraftNumberingModal');

                if (
                    showDraftNumberingModal &&
                    this.isAdmin &&
                    Object.values(newValue).some((count) => count > 0)
                ) {
                    this.open({
                        key: 'draftNumberingModal',
                        props: {},
                    });
                }
            },
            immediate: true,
        },
        buttons: function () {
            this.checkPermissionsButtons(this.buttons);
        },
    },

    created() {
        if (this.isMap) {
            this.presetForMapDisplay('map-view');
        }
        this.initListing();
        EventBus.on('listing:refresh', this.fetch);
        EventBus.on('email:sendDocument', this.handleSendDocumentByEmail);
        this.triggerOpenModalFromQuery();
    },

    beforeUnmount() {
        this.$store.unregisterModule(this.listinghash);
        EventBus.off('listing:refresh', this.fetch);
        EventBus.off('email:sendDocument', this.handleSendDocumentByEmail);
    },

    methods: {
        ...popupPanelMapActions(['open']),
        goToUrl,
        handle_function_call(fnName, parameters, disabled) {
            disabled ? null : this[fnName](parameters);
        },
        callBus(event, disable, data) {
            disable ? null : EventBus.emit(event, data);
        },
        onButtonClick(item) {
            if (item.disabled) {
                return;
            }

            if (item.type === 'vuejs') {
                this.callBus(item.url.event, item.disabled, {
                    linkedid: item.linkedid,
                    linkedtype: item.linkedtype,
                });
            } else if (item.type === 'function') {
                this.handle_function_call(
                    item.url.event,
                    {
                        linkedid: item.linkedid,
                        linkedtype: item.linkedtype,
                    },
                    item.disabled,
                );
            } else if (item.event !== undefined) {
                this.$emit(item.event);
            } else {
                this.goToUrl(this.getButtonHref(item));
            }
        },
        initListing() {
            this.listinghash = this.nameListing + '_' + guid();
            const store = this.$store;
            if (!(store && store.state && store.state[this.listinghash])) {
                let module = ListingModule;
                if (this.storeModule) {
                    module = this.storeModule;
                } else if (this.listingMapping) {
                    module = ApiListingModule;
                } else if (this.isMap) {
                    module = MapModule;
                }
                store.registerModule(this.listinghash, module);
            }

            this.$store.dispatch(`${this.listinghash}/updateListingHash`, this.listinghash);

            const payload = this.isMap
                ? { ...this.$props, name: this.nameListing }
                : { ...this.$props, favorite: this.$route.query.favorite };
            this.$store.dispatch(`${this.listinghash}/init`, payload).then(() => {
                this.fetch();
            });
        },
        // PAGINATION
        goToPage(page) {
            this.$store.commit(`${this.listinghash}/updateCurrentPage`, page);
            window.EventBus.emit('listing:updatePage', { listing: this.listinghash, page: page });
            this.fetch();
        },
        getButtonHref(button) {
            return buildUrl(button.href, [
                { key: 'linkedid', value: this.$store.getters.linkedid },
            ]);
        },
        // Main methods
        fetch(overwriteMeta) {
            this.$store.dispatch(`${this.listinghash}/fetch`, overwriteMeta).then(() => {
                if (this.activeFilters && this.activeFilters.length !== 0) {
                    this.$store.commit(`${this.listinghash}/updateFiltersView`, 'summary');
                }
                this.checkPermissionsButtons(this.buttons);
                this.$emit('listinghash', this.listinghash);
                this.$forceUpdate();
            });

            if (this.useFavorites) {
                this.$store.dispatch(`${this.listinghash}/fetchFavorites`);
            }
        },
        checkPermissionsButtons(buttons) {
            buttons.map((button) => {
                this.checkPermissionsButton(button);
            });
        },
        checkPermissionsButton(button) {
            const hasPermission = this.permissions && button.permission && button.permission !== '';
            const canPermission =
                hasPermission &&
                (Array.isArray(button.permission)
                    ? button.permission.every((permission) => this.permissions[permission] === true)
                    : this.permissions[button.permission] === true);
            const hasRelatedPermission =
                this.relatedPermissions &&
                button.relatedPermission &&
                button.relatedPermission !== '';
            const canRelatedPermission =
                hasRelatedPermission &&
                this.relatedPermissions[button.relatedPermission] &&
                this.relatedPermissions[button.relatedPermission] === true;
            if (hasPermission && hasRelatedPermission) {
                button.disabled = canPermission && canRelatedPermission ? false : true;
            } else if (hasPermission) {
                button.disabled = canPermission ? false : true;
            } else {
                button.disabled = false;
            }
            if (button.items && button.items.length > 0) {
                button.items.map((item) => {
                    this.checkPermissionsButton(item);
                });
            }
        },
        toggleKPI(e) {
            const display = { ...this.display };
            display.kpi = !display.kpi;

            // In case this listing has KPI, but not loaded on the page
            // we have to get them now
            if (display.kpi && this.hasKpis) {
                this.$store.dispatch(`${this.listinghash}/reloadKpis`);
            }

            if (this.$store.getters[`${this.listinghash}/isLoadingKpis`]) {
                const interval = setInterval(() => {
                    if (!this.$store.getters[`${this.listinghash}/isLoadingKpis`]) {
                        clearInterval(interval);
                        this.$store
                            .dispatch(`${this.listinghash}/updateDisplay`, display)
                            .then(() => {
                                this.$store.commit(`${this.listinghash}/updateDisplay`, display);
                            });
                    }
                }, 100);
            }

            if (!this.$store.getters[`${this.listinghash}/isLoadingKpis`]) {
                this.$store.dispatch(`${this.listinghash}/updateDisplay`, display).then(() => {
                    this.$store.commit(`${this.listinghash}/updateDisplay`, display);
                });
            }

            if (!display.kpi) {
                e.target.blur();
            }
        },

        presetForMapDisplay(idButton) {
            this.switchDisplay.map((display) => (display.selected = display.id == idButton));
            this.listingMapData = ConfigListings.getListingByName(this.name);
            this.nameListing = this.isMap ? this.listingMapData.mapname : this.name;
            this.activeToggleButton = idButton;
        },

        processChangeDisplay(idButton) {
            this.isMap = idButton === 'map-view';
            new LocalStorage(`${this.name}_listing`, 'viewmap').set(this.isMap);
            this.$store.unregisterModule(this.listinghash);
            this.presetForMapDisplay(idButton);
            this.initListing();
        },

        toggleMap(idButton) {
            this.processChangeDisplay(idButton);
        },

        openPurchaseInvoiceModal() {
            this.open({
                key: 'purchaseInvoiceModal',
            });
        },

        openOpportunity(params) {
            this.open({
                key: 'opportunityForm',
                props: {
                    oppId: false,
                    mode: 'edit',
                    action: 'edit',
                    linkedid: params.linkedid,
                    linkedtype: params.linkedtype,
                },
            });
        },
        exportButtonLabel(exportFormat) {
            return this.permissions.export
                ? this.$l10n.__('Export ' + exportFormat.toUpperCase())
                : this.$l10n.__('Export ' + exportFormat.toUpperCase());
        },
        handleExport(exportFormat) {
            if (!this.permissions.export) {
                this.$store.dispatch(`${this.listinghash}/permissionDenied`);
                return;
            }

            this.$store.commit(`${this.listinghash}/toggleDisplay`, 'export');
            this.$store.commit(`${this.listinghash}/updateExportType`, exportFormat);
        },
        triggerOpenModalFromQuery() {
            const modalQuery = this.$route.query?.open_modal;

            const modalHandlers = {
                purchaseInvoiceImport: this.openPurchaseInvoiceModal,
            };

            const handler = modalHandlers[modalQuery];

            if (!handler) {
                return;
            }

            handler();
        },
        async handleSendDocumentByEmail({
            docId,
            doctype,
            type = 'document',
            fileId = null,
            docIdent = null,
        }) {
            let docFile = null;
            if (fileId) {
                docFile = await ApiClient.get(`/files/${fileId}`);
            }

            await this.checkEmailProvider(
                {
                    template: {
                        type,
                        contact: {
                            id: docId,
                            type: doctype,
                        },
                        related: [
                            {
                                id: docId,
                                type: doctype,
                            },
                        ],
                    },
                    relatedtype: doctype,
                    relatedid: docId,
                },
                { checkPrefill: true },
                '',
                new ListingEmailRequest({
                    relatedId: docId,
                    relatedType: doctype,
                    docFile: docFile,
                    docLabel: docIdent,
                }),
            );
        },
    },
};
</script>

<style lang="scss" scoped>
@use '~@sellsy/styleguide/src/_variables' as *;
@import '~@sellsy/styleguide/src/_transitions';

.flex-row {
    .leftpane {
        width: 280px;
    }
}
.placeholder-alert {
    padding-top: 10px !important;
    padding-bottom: 10px !important;
    height: 40px;
}

.compass-toggle-button {
    &--is-active {
        border-color: var(--colors-common-border-default);
        color: var(--colors-common-text-standard-active);
        cursor: default;

        &:hover,
        &:focus {
            border-color: var(--colors-common-border-default);
            color: var(--colors-common-text-standard-active);
            cursor: default;
        }
    }
    &.upsell-action {
        background: var(--colors-component-background-actionable-disabled);
        color: var(--colors-common-text-standard-disabled);
        position: relative;

        &:hover,
        &:focus {
            background: var(--colors-component-background-actionable-disabled);
            border-color: var(--colors-common-border-default);
            color: var(--colors-common-text-standard-disabled);
        }

        :deep(.compass-feature-badge) {
            height: 12px;
            position: absolute;
            right: 2px;
            top: 2px;
            width: 12px;

            svg {
                height: 7.2px;
                width: 7.2px;
            }
        }
    }
}

.kip-list {
    margin: calc(var(--spacing-1) * -1) 0 var(--spacing-0);
}
</style>

<style lang="scss">
.listing-nav-actions-menu {
    z-index: 3;
}
</style>
